import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasCategoryContext;

export interface NISSearchTextStyles {
    container?: InjectableCss;
    title?: InjectableCss;
    titleWrapper?: InjectableCss;
    searchTerm?: TypographyProps;
}

export const nisSearchTextStyles: NISSearchTextStyles = {
    container: {
        css: css`
            width: 100%;
            margin-top: 24px;
        `,
    },
    titleWrapper: {
        css: css`
            margin-bottom: 8px;
        `,
    },
    title: {
        css: css`
            font-size: ${TYPOGRAPHY.size.large};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            color: ${COLORS.background.tag};
            font-family: "Roboto";
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: ${TYPOGRAPHY.size.smaller};
                        line-height: 16px;
                    `,
                    css`
                        font-size: ${TYPOGRAPHY.size.smaller};
                        line-height: 16px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    searchTerm: {
        variant: "h1",
        css: css`
            font-size: ${TYPOGRAPHY.size.base};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            color: ${getProp("theme.typography.h1.color")};
            text-align: left;
            font-family: "Roboto";
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: ${TYPOGRAPHY.size.xxlarge};
                            font-weight: ${TYPOGRAPHY.weight.bold};
                            line-height: 40px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
};

const styles = nisSearchTextStyles;

const NISSearchText = ({ category, query, location, isLoading }: Props) => {
    if ((category && !query) || (location?.pathname !== "/Search" && isLoading)) {
        return null;
    }

    return (
        <>
            {query && (
                <StyledWrapper {...styles.container}>
                    <StyledWrapper {...styles.titleWrapper}>
                        <Typography {...styles.title}>{`${translate("You searched...")}`}</Typography>
                    </StyledWrapper>
                    <StyledWrapper>
                        <Typography {...styles.searchTerm}> "{query}" </Typography>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => {
    const correctedQuery = getProductListDataViewProperty(state, "correctedQuery");
    const { productFilters, isLoading } = state.pages.productList;
    return {
        category: getCategoryState(state, ownProps.categoryId).value,
        query: correctedQuery || productFilters.query,
        location: getLocation(state),
        isLoading,
    };
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withCategory(NISSearchText)),
    definition: {
        group: "Categories",
        icon: "list-ul",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
